import React, { useEffect, useState, useCallback } from 'react';
import { 
  AreaChart, 
  Area, 
  XAxis, 
  YAxis, 
  Tooltip, 
  ResponsiveContainer, 
  CartesianGrid 
} from 'recharts';
import { DollarSign, Users, BookOpen, TrendingUp } from 'lucide-react';
import '../../styles/admin/Dashboard.css';

export default function Dashboard() {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalStudents: 0,
    activePrograms: 0,
    recentSales: 0
  });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const API_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5000');

  // Generate empty chart data for last 30 days
  const generateEmptyChartData = () => {
    const data = [];
    for (let i = 29; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      data.push({
        date: date.toISOString().split('T')[0],
        date_label: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        revenue: 0,
        purchases: 0
      });
    }
    return data;
  };

  const fetchStats = useCallback(async () => {
    // Set empty chart data initially so chart renders
    setChartData(generateEmptyChartData());
    
    try {
      // Fetch admin stats
      const statsRes = await fetch(`${API_URL}/api/admin/stats`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });
      
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        if (statsData.success) {
          setStats({
            totalRevenue: statsData.stats.totalRevenue || 0,
            totalStudents: statsData.stats.totalUsers || 0,
            activePrograms: statsData.stats.totalPrograms || 0,
            recentSales: statsData.stats.totalPurchases || 0
          });
        }
      }

      // Fetch daily sales data for chart
      const salesRes = await fetch(`${API_URL}/api/admin/daily-sales`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (salesRes.ok) {
        const salesData = await salesRes.json();
        if (salesData.success && salesData.data && salesData.data.length > 0) {
          setChartData(salesData.data);
        }
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
      // Keep the empty chart data so chart still renders
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchStats();
    // Refresh every 30 seconds
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const summaryCards = [
    { 
      title: 'Total Revenue', 
      value: `₹${stats.totalRevenue.toLocaleString()}`, 
      icon: DollarSign, 
      sub: 'All time' 
    },
    { 
      title: 'Total Students', 
      value: stats.totalStudents.toString(), 
      icon: Users, 
      sub: 'Registered users' 
    },
    { 
      title: 'Active Programs', 
      value: stats.activePrograms.toString(), 
      icon: BookOpen, 
      sub: 'Published' 
    },
    { 
      title: 'Total Purchases', 
      value: stats.recentSales.toString(), 
      icon: TrendingUp, 
      sub: 'All time' 
    },
  ];

  return (
    <div className="admin-dashboard-content">
      <div className="dashboard-header">
        <h1>Dashboard</h1>
        <p>Welcome back, here's what's happening.</p>
      </div>

      {/* Summary Cards */}
      <div className="stats-grid">
        {summaryCards.map(({ title, value, icon: Icon, sub }) => (
          <div key={title} className="stat-card">
            <div className="stat-card-header">
              <span className="stat-card-title">{title}</span>
              <div className="stat-card-icon">
                <Icon className="h-5 w-5" />
              </div>
            </div>
            <div className="stat-card-value">{loading ? '—' : value}</div>
            <p className="stat-card-sub">{sub}</p>
          </div>
        ))}
      </div>

      {/* Sales Chart */}
      <div className="chart-card">
        <div className="chart-header">
          <h3>Sales — Last 30 Days</h3>
        </div>
        <div className="chart-container">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis
                dataKey="date_label"
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickLine={false}
                axisLine={false}
                interval={4}
              />
              <YAxis
                tick={{ fontSize: 11, fill: '#94a3b8' }}
                tickLine={false}
                axisLine={false}
                domain={[0, 'auto']}
                ticks={[0, 2000, 4000, 6000, 8000, 10000]}
                tickFormatter={v => {
                  if (v >= 1000) {
                    return `₹${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}k`;
                  }
                  return `₹${v}`;
                }}
              />
              <Tooltip
                formatter={(v) => {
                  const value = Number(v);
                  if (value >= 1000) {
                    return [`₹${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}k`, 'Revenue'];
                  }
                  return [`₹${value}`, 'Revenue'];
                }}
                contentStyle={{ 
                  borderRadius: 8, 
                  border: '1px solid #e2e8f0', 
                  background: '#fff',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="revenue" 
                stroke="#3b82f6" 
                strokeWidth={2}
                fillOpacity={1} 
                fill="url(#colorRevenue)" 
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
